import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import log from "../../utils/logger";

const RegistrationForm: React.FC = () => {
  const { user, loading: authLoading, checkUserAccess } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    address: "",
    gender: "prefer-not-to-say" as "male" | "female" | "other" | "prefer-not-to-say",
    education: "",
    experience: "",
    requestedRole: "reporter" as "reporter" | "co-editor" | "chief-editor",
  });

  // Note: Status checks are handled by the firewall in App.tsx PrivateRoute

  // Check if user already submitted registration
  React.useEffect(() => {
    if (user?.status === "pending" && user?.requestedRole) {
      setAlreadySubmitted(true);
      setSuccess(false); // Don't show success message if already submitted
    } else {
      setAlreadySubmitted(false);
    }
  }, [user]);

  // Debug logging
  React.useEffect(() => {
    log.debug("RegistrationForm - User state:", user);
    log.debug("RegistrationForm - Auth loading:", authLoading);
    log.debug("RegistrationForm - Already submitted:", alreadySubmitted);
    log.debug("RegistrationForm - Success:", success);
  }, [user, authLoading, alreadySubmitted, success]);

  // Show loading state while auth is checking
  if (authLoading) {
    return (
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show error if user is not available
  if (!user) {
    return (
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
            <Alert severity="error">
              You must be logged in to request access. Please log in first.
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Note: Approved users are handled by the firewall and won't reach this component


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      log.debug("RegistrationForm - Submitting form data:", formData);
      const result = await api.requestAccess(formData);
      log.debug("RegistrationForm - API response:", result);

      if (result.success) {
        setSuccess(true);
        // Refresh user access check to update user state
        // This will update the user with status="pending" and requestedRole
        setTimeout(async () => {
          // Call checkUserAccess to refresh user state without full page reload
          await checkUserAccess();
        }, 1000);
      } else {
        setError(result.error || "Failed to submit access request");
      }
    } catch (err: any) {
      log.error("RegistrationForm - Error submitting form:", err);
      setError(err.message || "Failed to submit access request");
    } finally {
      setLoading(false);
    }
  };

  // Note: Pending users with requestedRole are handled by the firewall and shown StatusPage

  // Ensure user exists and has email before rendering
  if (!user || !user.email) {
    return (
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
            <Alert severity="error">
              Unable to load user information. Please try logging in again.
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Request Access
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Please fill in your details to request access to the platform
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  value={user?.email || ""}
                  disabled
                  helperText="Email from your Google account"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="mobile"
                  label="Mobile Number"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  autoComplete="tel"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  select
                  id="gender"
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="address"
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="education"
                  label="Education"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  placeholder="e.g., Bachelor's in Journalism, Master's in Communication"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="experience"
                  label="Experience (Optional)"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="Describe your relevant work experience"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  select
                  id="requestedRole"
                  label="Requested Role"
                  name="requestedRole"
                  value={formData.requestedRole}
                  onChange={handleChange}
                  helperText="Select the role you are requesting access for"
                >
                  <MenuItem value="reporter">Reporter</MenuItem>
                  <MenuItem value="co-editor">Co-Editor</MenuItem>
                  <MenuItem value="chief-editor">Chief Editor</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Submit Access Request"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegistrationForm;

